<template>
  <div
    class="node-option cursor-pointer"
    :class="{
      'node-option--selected': selected,
    }"
    @click="handleSelect"
  >
    <div class="node-option__header">
      <h5 class="heading-h7 node-option__order">
        {{ typeName }}
        {{ dataSource.type === NodeType.ListSectionItem ? `${parent.order}.` : "" }}{{ showOrder ? order : '' }}
        <span class="acronym">{{ nodeIdentifier }}</span>

        <a-tooltip
          v-if="showQueueWarning"
          title="Este campo se encontraba utilizando una cola que ha sido eliminada. Podrás volver a activar tu chatbot cuando selecciones una nueva cola de transferencia."
          placement="topLeft"
          arrowPointAtCenter
        >
          <WarningFilled style="color: #faad14" />
        </a-tooltip>
        <a-tooltip
          v-else-if="node?.deleted_variable"
          title="Este campo se encuentra sin una variable, selecciona una para poder activar tu chatbot."
          placement="topLeft"
          arrowPointAtCenter
        >
          <WarningFilled style="color: #faad14" />
        </a-tooltip>
        <links-attachments :dataSource="dataSource" @onPreview="handleShowMedia" />
      </h5>
      <em v-if="dataSource.type !== NodeType.ListSection" class="info-2 node-option__action">
        <a-tooltip placement="right">
          <template #title v-if="hasActionTooltip">
            {{ `${action} “${dataSource?.queue?.name}”` }}
          </template>

          {{ `${action}${formattedName}` }}
        </a-tooltip>
      </em>
    </div>
    <div class="node-option__content">
      <p class="body-2 node-option__description mrg-bottom-0 text--left" :title="description">
        {{ description }}
      </p>
      <div 
        class="node-option__actions actions"
        v-if="![NodeType.WrongOptionAPI, NodeType.OptionConsultApi].includes(dataSource.type)"
      >
        <a-dropdown :trigger="['click']" placement="topRight" :disabled="disabled">
          <a-button type="link" class="button" @click.stop :disabled="disabled">
            Cambiar de posición
          </a-button>

          <template #overlay v-if="quantityOrders !== 0">
            <a-menu>
              <a-menu-item
                v-for="item in quantityOrders"
                :key="item"
                @click="$emit('onChangeOrder', item)"
                :disabled="item === order || !isAllowedChange"
              >
                {{ item === order ? `Posición ${item} (actual)` : `Posición ${item}` }}
              </a-menu-item>
            </a-menu>
          </template>
        </a-dropdown>
        <template v-if="showDelete">
          <a-divider class="divider" type="vertical" />
          <a-button
            type="link"
            :danger="!disabled"
            class="button"
            @click.stop="$emit('onDelete')"
            :disabled="disabled"
          >
            Eliminar
          </a-button>
        </template>
      </div>
    </div>
  </div>
</template>
<script>
import { WarningFilled } from "@ant-design/icons-vue";

import { BotActions, NodeType } from "@/app/chatbots/utils/enums";
import { inject } from "vue";
import LinksAttachments from "@/app/chatbots/components/molecules/LinksAttachments.vue";
import { AcronymByNode } from "@/app/chatbots/utils/texts";

export default {
  name: "OptionNode",
  components: {
    WarningFilled,
    LinksAttachments,
  },
  props: {
    order: { type: Number, default: 1 },
    description: { type: String, default: "Descripcion" },
    quantityOrders: { type: Number, required: false, default: 0 },
    dataSource: { type: Object, required: false },
    selected: { type: Boolean, required: false, default: false },
    showDelete: { type: Boolean, required: false, default: true },
    parent: Object,
    action: { type: String, default: "Finalizar chatbot" },
    isAllowedChange: { type: Boolean, required: false, default: true },
    disabled: { type: Boolean, default: false },
  },
  setup() {
    const fetchChatbot = inject("fetchChatbot");

    return { fetchChatbot, NodeType };
  },
  methods: {
    handleSelect() {
      this.$emit("onSelect");
    },
    handleShowMedia(mediaData) {
      this.$emit("onPreviewMedia", mediaData);
    },
  },
  computed: {
    isTransferAction() {
      return this.dataSource?.action === BotActions.TransferToQueue;
    },
    hasActionTooltip() {
      return this.dataSource?.queue?.name?.length > 25 && this.isTransferAction;
    },
    showQueueWarning() {
      return this.isTransferAction && this.dataSource?.deleted_queue;
    },
    formattedName() {
      if (!this.isTransferAction || this.showQueueWarning) return "";

      const { queue } = this.dataSource;
      const formattedName =
        queue?.name?.length > 25 ? `${queue?.name?.substring(0, 25)}...` : queue?.name;

      return formattedName ? ` “${formattedName}”` : "";
    },
    typeName() {
      if (this.dataSource.type === NodeType.SingleOption) return `${this.order}. Opción`;
      if (this.dataSource.type === NodeType.ButtonOption) return `${this.order}. Botón`;
      if (this.dataSource.type === NodeType.ListSection) return `${this.order}. Sección`;
      if (this.dataSource.type === NodeType.ListSectionItem) return `${this.order}. Item`;
      console.log("this.dataSource: ", this.dataSource);
      if (this.dataSource.type === NodeType.OptionConsultApi) 
        if (this.dataSource.option) return `${this.order}. validación_de_cliente - ${this.dataSource.referencial_name}`;
        else return `Respuesta de cliente`;
      if (this.dataSource.type === NodeType.WrongOptionAPI) return 'Opción error en API';
      return "Error";
    },
    showOrder() {
      return ![NodeType.OptionConsultApi, NodeType.WrongOptionAPI].includes(this.dataSource.type);
    },
    nodeIdentifier() {
      const acronym = AcronymByNode[this.dataSource.type];
      const nodeId = this.dataSource.id;

      if (!acronym) return "";

      return `(${acronym}${nodeId})`;
    },
  },
  watch: {
    showQueueWarning(newVal, prevVal) {
      if (prevVal) this.fetchChatbot();
    },
  },
};
</script>
<style lang="sass" scoped>
.node-option
  margin-bottom: 8px
  border: 1px solid $gray-5
  border-radius: 8px
  padding: 12px
  &__header
    display: flex
    flex-direction: row
    justify-content: space-between
    margin-bottom: 4px
  &__order
    text-align: left
    margin-bottom: 2px
  &__content
    display: flex
    justify-content: space-between
  &__description
    text-overflow: ellipsis
    overflow: hidden
    text-wrap: nowrap
    color: $gray-8
  &--selected
    border: 1px solid $primary
    background-color: $blue-1
  &:hover
    border: 1px solid $primary
  &__action
    color: $gray-7
    &--danger
      color: $red-6

.divider
  background-color: #D9D9D9

.actions
  flex: 0 0 auto

.cursor-pointer
  cursor: pointer

.heading-h7
  color: $gray-9
  font-weight: 600
  font-size: 14px

h5, p
  margin-bottom: 0

.button
  padding: 0
  height: 100%

.acronym
  font-size: 12px
  font-weight: 400
  color: $text-tertiary
</style>
